// Governed APPROVED platform copy for Creative Studio — the client-approved
// final ad copy imported from a workbook (e.g. Final_NSC_Theatrical_Ad_Copy_SMG.xlsx),
// versioned and auditable per campaign + platform. Distinct from AI-generated
// or manually drafted WORKING copy, which this layer never touches.
//
// APPROVED_COPY_STRUCTURES / APPROVED_COPY_STATES mirror the backend
// authority (backend services/approvedPlatformCopyStructure.js) and are
// parity-locked by the backend's creativeCopySpecsMirrorParity test; keep
// both top-level constants dependency-free literals. Every text slot binds
// to a canonical platform copy-spec field (creativeCopyPlatformSpecs) — this
// layer defines NO character limits of its own, and the workbook is never
// the validator.

import { getCopyFieldSpec } from "./creativeCopyPlatformSpecs";

export const APPROVED_COPY_STATES = Object.freeze({
  NO_APPROVED_COPY: "no_approved_copy",
  APPROVED_READY: "approved_ready",
  APPROVED_NEEDS_CORRECTION: "approved_needs_correction",
  SUPERSEDED: "superseded",
});

export const APPROVED_COPY_STRUCTURES = Object.freeze({
  tiktok: Object.freeze({
    platform: "tiktok",
    shape: "creatives",
    creativeLabel: "Creative",
    maxCreatives: 10,
    creativeFields: Object.freeze([
      Object.freeze({ key: "primaryText", label: "Primary Text", specField: "primaryText" }),
      Object.freeze({ key: "cta", label: "CTA", specField: "cta" }),
      Object.freeze({ key: "destinationUrl", label: "Destination URL", type: "url", required: true }),
    ]),
    source: "baseline_platform_guidance_v1_confirm_before_activation",
  }),
  youtube: Object.freeze({
    platform: "youtube",
    shape: "groups",
    destinationField: Object.freeze({ key: "finalUrl", label: "Final URL", type: "url", required: true }),
    groups: Object.freeze([
      Object.freeze({ key: "headlines", label: "Headlines", itemLabel: "Headline", specField: "headline", maxItems: 5, minItems: 1 }),
      Object.freeze({ key: "longHeadlines", label: "Long Headlines", itemLabel: "Long Headline", specField: "longHeadline", maxItems: 5, minItems: 0 }),
      Object.freeze({ key: "descriptions", label: "Descriptions", itemLabel: "Description", specField: "description", maxItems: 5, minItems: 0 }),
    ]),
    source: "baseline_platform_guidance_v1_confirm_before_activation",
  }),
});

export const APPROVED_COPY_STATE_LABELS = Object.freeze({
  [APPROVED_COPY_STATES.NO_APPROVED_COPY]: "No approved copy",
  [APPROVED_COPY_STATES.APPROVED_READY]: "Client-approved · Ready",
  [APPROVED_COPY_STATES.APPROVED_NEEDS_CORRECTION]: "Client-approved · Needs correction",
  [APPROVED_COPY_STATES.SUPERSEDED]: "Superseded",
});

export const APPROVED_COPY_DIFF_STATE_LABELS = Object.freeze({
  unchanged: "Unchanged",
  new: "New",
  changed: "Changed",
  invalid: "Invalid",
  removed: "Removed",
});

export function getApprovedCopyStructure(platformKey) {
  return APPROVED_COPY_STRUCTURES[String(platformKey || "")] || null;
}

function charCount(value) {
  // Unicode code points, matching the canonical spec layer and the backend.
  return Array.from(String(value ?? "")).length;
}

// Destination/click-through URL governance — mirrors the backend rule: http(s)
// only, real hostname; normalization preserves path/query.
export function validateDestinationUrl(value, { required = true } = {}) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return { value: trimmed, normalizedUrl: null, status: required ? "missing" : "empty" };
  }
  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch (_error) {
    return { value: trimmed, normalizedUrl: null, status: "invalid_url" };
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return { value: trimmed, normalizedUrl: null, status: "invalid_url" };
  }
  if (!parsed.hostname || !parsed.hostname.includes(".")) {
    return { value: trimmed, normalizedUrl: null, status: "invalid_url" };
  }
  return { value: trimmed, normalizedUrl: parsed.href, status: "ok" };
}

function limitsForSlot(platformKey, slot) {
  if (slot.specField) {
    const specField = getCopyFieldSpec(platformKey, slot.specField);
    if (specField) {
      return {
        maxChars: specField.maxChars ?? null,
        recommendedChars: specField.recommendedChars ?? null,
        required: slot.required ?? Boolean(specField.required),
      };
    }
  }
  return {
    maxChars: slot.maxChars ?? null,
    recommendedChars: slot.recommendedChars ?? null,
    required: Boolean(slot.required),
  };
}

function validateTextSlot(platformKey, slot, path, label, value) {
  const { maxChars, recommendedChars, required } = limitsForSlot(platformKey, slot);
  const count = charCount(value);
  const hasValue = count > 0;
  let status = "ok";
  if (required && !hasValue) status = "missing";
  else if (maxChars != null && count > maxChars) status = "over_max";
  else if (recommendedChars != null && count > recommendedChars) status = "over_recommended";
  else if (!hasValue) status = "empty";
  return { path, label, value: String(value ?? ""), count, maxChars, recommendedChars, required, status };
}

function validateUrlSlot(slot, path, label, value) {
  const result = validateDestinationUrl(value, { required: slot.required !== false });
  return {
    path,
    label,
    value: result.value,
    normalizedUrl: result.normalizedUrl,
    count: charCount(result.value),
    maxChars: null,
    recommendedChars: null,
    required: slot.required !== false,
    status: result.status,
  };
}

const ERROR_STATUSES = ["missing", "over_max", "invalid_url"];

// Mirrors the backend validator: flat field list with paths like
// "creatives[0].primaryText" / "headlines[2]" / "finalUrl".
export function validateApprovedCopyPayload(platformKey, payload) {
  const structure = getApprovedCopyStructure(platformKey);
  if (!structure) {
    return { platform: platformKey || null, recognized: false, fields: [], hasErrors: true, errorCount: 1, state: APPROVED_COPY_STATES.APPROVED_NEEDS_CORRECTION };
  }

  const fields = [];
  if (structure.shape === "creatives") {
    const creatives = Array.isArray(payload?.creatives) ? payload.creatives : [];
    if (!creatives.length) {
      fields.push({ path: "creatives", label: structure.creativeLabel, value: "", count: 0, maxChars: null, recommendedChars: null, required: true, status: "missing" });
    }
    creatives.forEach((creative, index) => {
      for (const slot of structure.creativeFields) {
        const path = `creatives[${index}].${slot.key}`;
        const label = `${structure.creativeLabel} ${index + 1} ${slot.label}`;
        if (slot.type === "url") fields.push(validateUrlSlot(slot, path, label, creative?.[slot.key]));
        else fields.push(validateTextSlot(structure.platform, slot, path, label, creative?.[slot.key]));
      }
    });
  } else if (structure.shape === "groups") {
    const destination = structure.destinationField;
    fields.push(validateUrlSlot(destination, destination.key, destination.label, payload?.[destination.key]));
    for (const group of structure.groups) {
      const items = Array.isArray(payload?.[group.key]) ? payload[group.key] : [];
      if (items.length < (group.minItems ?? 0)) {
        fields.push({ path: group.key, label: group.label, value: "", count: 0, maxChars: null, recommendedChars: null, required: true, status: "missing" });
      }
      items.forEach((item, index) => {
        const path = `${group.key}[${index}]`;
        const label = `${group.itemLabel} ${index + 1}`;
        fields.push(validateTextSlot(structure.platform, group, path, label, item));
      });
    }
  }

  const errorCount = fields.filter((f) => ERROR_STATUSES.includes(f.status)).length;
  const hasErrors = errorCount > 0;
  return {
    platform: structure.platform,
    recognized: true,
    fields,
    hasErrors,
    errorCount,
    state: hasErrors
      ? APPROVED_COPY_STATES.APPROVED_NEEDS_CORRECTION
      : APPROVED_COPY_STATES.APPROVED_READY,
  };
}

// Readiness for the Platform Copy Specs card: the governed approved payload —
// not any working-copy textarea — is the authoritative readiness input where
// a current approved version exists.
export function evaluateApprovedCopyReadiness(currentVersion) {
  if (!currentVersion || !currentVersion.payload) {
    return { state: APPROVED_COPY_STATES.NO_APPROVED_COPY, label: APPROVED_COPY_STATE_LABELS[APPROVED_COPY_STATES.NO_APPROVED_COPY], badge: "guidance", validation: null };
  }
  const validation = validateApprovedCopyPayload(currentVersion.platform, currentVersion.payload);
  const state = currentVersion.status === "superseded" ? APPROVED_COPY_STATES.SUPERSEDED : validation.state;
  return {
    state,
    label: APPROVED_COPY_STATE_LABELS[state],
    badge: state === APPROVED_COPY_STATES.APPROVED_READY ? "deployable" : "needs-edit",
    validation,
  };
}

// Flatten a payload for display/edit: [{path, label, value, kind}].
export function flattenApprovedCopyPayload(platformKey, payload) {
  const structure = getApprovedCopyStructure(platformKey);
  if (!structure || !payload) return [];
  const rows = [];
  if (structure.shape === "creatives") {
    (payload.creatives || []).forEach((creative, index) => {
      for (const slot of structure.creativeFields) {
        rows.push({
          path: `creatives[${index}].${slot.key}`,
          label: `${structure.creativeLabel} ${index + 1} · ${slot.label}`,
          value: String(creative?.[slot.key] ?? ""),
          kind: slot.type === "url" ? "url" : "text",
        });
      }
    });
  } else if (structure.shape === "groups") {
    rows.push({
      path: structure.destinationField.key,
      label: structure.destinationField.label,
      value: String(payload?.[structure.destinationField.key] ?? ""),
      kind: "url",
    });
    for (const group of structure.groups) {
      (payload?.[group.key] || []).forEach((item, index) => {
        rows.push({
          path: `${group.key}[${index}]`,
          label: `${group.itemLabel} ${index + 1}`,
          value: String(item ?? ""),
          kind: "text",
        });
      });
    }
  }
  return rows;
}

export function applyOperatorEditsToPayload(platformKey, payload, edits) {
  const structure = getApprovedCopyStructure(platformKey);
  if (!structure || !payload) return payload;
  const clone = JSON.parse(JSON.stringify(payload));
  for (const edit of Array.isArray(edits) ? edits : []) {
    if (String(edit?.platform || "") !== structure.platform) continue;
    const path = String(edit?.path || "");
    const creativeMatch = /^creatives\[(\d+)\]\.(primaryText|cta|destinationUrl)$/.exec(path);
    if (creativeMatch && clone.creatives?.[Number(creativeMatch[1])]) {
      clone.creatives[Number(creativeMatch[1])][creativeMatch[2]] = String(edit.value ?? "");
      continue;
    }
    if (path === "finalUrl" && clone.finalUrl !== undefined) {
      clone.finalUrl = String(edit.value ?? "");
      continue;
    }
    const groupMatch = /^(headlines|longHeadlines|descriptions)\[(\d+)\]$/.exec(path);
    if (groupMatch && Array.isArray(clone[groupMatch[1]]) && Number(groupMatch[2]) < clone[groupMatch[1]].length) {
      clone[groupMatch[1]][Number(groupMatch[2])] = String(edit.value ?? "");
    }
  }
  return clone;
}

// Rows for the governed import preview: the workbook payload with operator
// edits applied, re-validated locally, joined with the server diff states.
export function buildApprovedCopyPreviewRows(platformPreview, edits) {
  if (!platformPreview) return { rows: [], validation: null };
  const effectivePayload = applyOperatorEditsToPayload(
    platformPreview.platform,
    platformPreview.payload,
    edits
  );
  const validation = validateApprovedCopyPayload(platformPreview.platform, effectivePayload);
  const statusByPath = new Map(validation.fields.map((f) => [f.path, f]));
  const serverDiffByPath = new Map((platformPreview.diff || []).map((d) => [d.path, d]));
  const editByPath = new Map(
    (Array.isArray(edits) ? edits : [])
      .filter((e) => String(e?.platform || "") === platformPreview.platform)
      .map((e) => [e.path, e])
  );

  const rows = flattenApprovedCopyPayload(platformPreview.platform, effectivePayload).map((row) => {
    const check = statusByPath.get(row.path) || null;
    const serverDiff = serverDiffByPath.get(row.path) || null;
    const edit = editByPath.get(row.path) || null;
    const invalid = check ? ERROR_STATUSES.includes(check.status) : false;
    let state = serverDiff?.state || "new";
    if (invalid) state = "invalid";
    else if (edit && serverDiff) {
      state = serverDiff.currentValue === row.value ? "unchanged" : serverDiff.currentValue == null ? "new" : "changed";
    }
    return {
      ...row,
      state,
      currentValue: serverDiff?.currentValue ?? null,
      workbookValue: edit ? edit.workbookValue : null,
      edited: Boolean(edit),
      check,
    };
  });

  const removed = (platformPreview.diff || [])
    .filter((entry) => entry.state === "removed")
    .map((entry) => ({
      path: entry.path,
      label: entry.path,
      value: "",
      kind: "text",
      state: "removed",
      currentValue: entry.currentValue,
      workbookValue: null,
      edited: false,
      check: null,
    }));

  return { rows: [...rows, ...removed], validation };
}
